import { useState } from 'react'

import { useBulkRemove } from '@/api/hooks'
import { Button, Dialog, toast } from '@/components/ui'

import { errorMessage } from './errorMessage'
import { trackCountLabel } from './labels'

export interface BulkRemoveDialogProps {
  /** Library item ids, captured when the dialog opened. */
  ids: number[]
  onClose: () => void
  onRemoved: () => void
}

/**
 * Confirm removing a selection. Mounted only while there is something to
 * confirm, so the checkbox and any error reset between uses. The wording
 * matches RemoveTrackDialog — the choice about the files on disk is the same
 * choice, whether it is one track or forty.
 */
export function BulkRemoveDialog({ ids, onClose, onRemoved }: BulkRemoveDialogProps) {
  const [keepFile, setKeepFile] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const remove = useBulkRemove()

  function confirm() {
    setError(null)
    remove.mutate(
      { ids, deleteFile: !keepFile },
      {
        onSuccess: (result) => {
          const missed = ids.length - result.removed
          toast.success(
            `Removed ${trackCountLabel(result.removed)}`,
            missed > 0
              ? `${missed} could not be removed and ${missed === 1 ? 'is' : 'are'} still in your library.`
              : keepFile
                ? 'The audio files are still in your library folder.'
                : undefined,
          )
          onRemoved()
          onClose()
        },
        onError: (cause) => setError(errorMessage(cause)),
      },
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !remove.isPending) onClose()
      }}
      size="sm"
      title="Remove from library"
      description={`${trackCountLabel(ids.length)} will no longer appear in your library.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirm} loading={remove.isPending}>
            Remove
          </Button>
        </>
      }
    >
      <label className="flex min-h-11 items-center gap-3">
        <input
          type="checkbox"
          checked={keepFile}
          onChange={(event) => setKeepFile(event.target.checked)}
          className="h-4 w-4 shrink-0 accent-accent"
        />
        <span className="text-sm font-medium text-text">Keep the files on disk</span>
      </label>

      <p className="mt-1 text-sm text-text-muted">
        {keepFile
          ? 'The audio files stay in your library folder on the server. Only the entries in Tunes are removed.'
          : 'The audio files will be deleted from the server and the space freed.'}
      </p>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </Dialog>
  )
}
